import * as configcat from '@configcat/sdk/browser'
import logger from '@missionlabs/logger'
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { configCatLogger } from './configCatLogger'

export type FlagAttributes = Record<string, string>

export type FeatureFlag = {
    enabled: boolean
}

export interface FeatureFlagProviderProps {
    sdkKey: string
    attributes?: FlagAttributes
}

type FeatureFlagContextValue = {
    flags: Record<string, boolean>
    identity?: string
    identify: (userId: string, attributes?: FlagAttributes) => Promise<void>
    logout: () => Promise<void>
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined)

const createUser = (userId: string, attributes: FlagAttributes, extra: FlagAttributes) =>
    new configcat.User(userId, undefined, undefined, { ...attributes, ...extra })

export const FeatureFlagProvider = ({
    sdkKey,
    attributes,
    children
}: PropsWithChildren<FeatureFlagProviderProps>) => {
    const [flags, setFlags] = useState<Record<string, boolean>>({})
    const [identity, setIdentity] = useState<string | undefined>()
    const clientRef = useRef<configcat.IConfigCatClient | undefined>(undefined)
    const userRef = useRef<configcat.User | undefined>(undefined)
    const attributesRef = useRef<FlagAttributes>(attributes ?? {})
    const extraAttributesRef = useRef<FlagAttributes>({})
    const requestIdRef = useRef(0)

    attributesRef.current = attributes ?? {}

    const loadFlags = useCallback(async () => {
        const client = clientRef.current
        const user = userRef.current
        const requestId = ++requestIdRef.current

        if (!client) {
            setFlags({})
            return
        }

        try {
            const values = await client.getAllValuesAsync(user)
            if (requestId !== requestIdRef.current) return

            const next: Record<string, boolean> = {}
            for (const setting of values) {
                next[setting.settingKey] = setting.settingValue === true
            }
            setFlags(next)
        } catch (error) {
            logger.error('Failed to read feature flags', error)
        }
    }, [])

    useEffect(() => {
        if (!sdkKey) {
            clientRef.current = undefined
            setFlags({})
            return
        }

        const client = configcat.getClient(sdkKey, configcat.PollingMode.AutoPoll, {
            dataGovernance: configcat.DataGovernance.EuOnly,
            logger: configCatLogger
        })
        clientRef.current = client

        if (userRef.current) client.setDefaultUser(userRef.current)

        const onConfigChanged = () => {
            void loadFlags()
        }

        client.on('configChanged', onConfigChanged)
        void loadFlags()

        return () => {
            client.off('configChanged', onConfigChanged)
            clientRef.current = undefined
            client.dispose()
        }
    }, [loadFlags, sdkKey])

    const identify = useCallback(
        async (userId: string, extra: FlagAttributes = {}) => {
            extraAttributesRef.current = extra
            const user = createUser(userId, attributesRef.current, extra)
            userRef.current = user
            clientRef.current?.setDefaultUser(user)
            setIdentity(userId)
            await loadFlags()
        },
        [loadFlags]
    )

    const logout = useCallback(async () => {
        extraAttributesRef.current = {}
        userRef.current = undefined
        clientRef.current?.clearDefaultUser()
        setIdentity(undefined)
        await loadFlags()
    }, [loadFlags])

    const value: FeatureFlagContextValue = useMemo(
        () => ({
            flags,
            identity,
            identify,
            logout
        }),
        [flags, identity, identify, logout]
    )

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>
}

const useFeatureFlagContext = () => {
    const context = useContext(FeatureFlagContext)
    if (!context) {
        throw new Error('useFlags must be used within FeatureFlagProvider')
    }
    return context
}

export const useFeatureFlags = () => {
    const { identity, identify, logout } = useFeatureFlagContext()
    return { identity, identify, logout }
}

export function useFlags<K extends string>(keys: readonly K[]): Record<K, FeatureFlag> {
    const { flags } = useFeatureFlagContext()
    const result = {} as Record<K, FeatureFlag>

    for (const key of keys) {
        result[key] = { enabled: flags[key] ?? false }
    }

    return result
}
